/**
 * One programme's history, newest first and grouped by day.
 *
 * Every kind of event the programme's audit trail holds: revisions, workflow
 * moves, review and approval decisions, exports and publications. Each kind
 * carries its own icon and badge, and an event recorded while impersonating
 * names both the person shown and the operator behind them.
 *
 * @module components/history/ProgrammeHistory
 */

import React, { useState } from "react";

import { useAuditTrail } from "@/components/admin/useAuditTrail";
import { auditActorLabel } from "@/components/shared/audit-actor";
import {
  AUDIT_CATEGORY_PRESENTATION,
  auditEventCategory,
} from "@/components/shared/audit-event-categories";
import { auditEventLabel } from "@/components/shared/audit-event-labels";
import { type AuditModuleName, auditTargetLabel } from "@/components/shared/audit-target";
import { TONE_BADGE } from "@/components/shared/tone-presentation";
import {
  Badge,
  Button,
  Combobox,
  EmptyState,
  ErrorAlert,
  FilterBar,
  FormField,
  Heading,
  Icon,
  MutedText,
  Panel,
  SelectField,
  Text,
  TextField,
  Time,
} from "@/components/ui";
import { Bone } from "@/components/ui/SkeletonParts";
import type { ProgrammePlatformClient } from "@/platform";
import type { AuditEvent } from "@/server/types";

import {
  groupByDay,
  HISTORY_CATEGORIES,
  historyEventDetail,
  historyFiltersActive,
  type HistoryFilterValues,
  historyQueryFor,
  historyRangeBackwards,
  NO_HISTORY_FILTERS,
} from "./programme-history";

/** How many events one page adds. */
const PAGE = 50;

/** The server's row cap; a longer history is narrowed by date instead. */
const MAX_EVENTS = 1000;

const NO_MODULES: ReadonlyMap<string, AuditModuleName> = new Map();

const CATEGORY_OPTIONS = [
  { value: "", label: "Every kind" },
  ...HISTORY_CATEGORIES.map((category) => ({
    value: category,
    label: AUDIT_CATEGORY_PRESENTATION[category].label,
  })),
];

export interface ProgrammeHistoryProps {
  client: ProgrammePlatformClient;
  programmeId: string;
  programmeTitle: string;
  /** The people the actor filter offers. */
  users: readonly { id: string; displayName: string }[];
  /** Module id to title, for events about this programme's modules. */
  moduleNames?: ReadonlyMap<string, AuditModuleName>;
}

function HistoryEntry({
  event,
  userNames,
  subject,
}: {
  event: AuditEvent;
  userNames: ReadonlyMap<string, string>;
  /** What the event was about, when it was not the programme itself. */
  subject: string | undefined;
}): React.ReactElement {
  const category = auditEventCategory(event.action);
  const presentation = AUDIT_CATEGORY_PRESENTATION[category];
  const detail = historyEventDetail(event);
  return (
    <li
      className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 py-3 sm:grid-cols-[auto_1fr_auto]"
      data-testid="history-event"
      data-category={category}
    >
      <Icon name={presentation.icon} size="lg" className="mt-0.5 text-muted" aria-hidden />
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <Text as="span" className="font-medium">
            {auditEventLabel(event.action)}
          </Text>
          <Badge tone={TONE_BADGE[presentation.tone]} size="sm" variant="soft">
            {presentation.label}
          </Badge>
        </div>
        <MutedText as="p" size="caption" className="mt-0.5" data-testid="history-event-actor">
          {auditActorLabel(event, userNames)}
          {subject ? ` · ${subject}` : null}
        </MutedText>
        {detail ? (
          <MutedText as="p" size="caption" className="mt-0.5 break-words">
            {detail}
          </MutedText>
        ) : null}
      </div>
      <MutedText
        as="span"
        size="caption"
        className="col-start-2 tabular-nums sm:col-start-3 sm:row-start-1"
      >
        <Time value={event.timestamp} />
      </MutedText>
    </li>
  );
}

/**
 * @example
 * ```tsx
 * <ProgrammeHistory client={client} programmeId={id} programmeTitle={title} users={users} />
 * ```
 */
export function ProgrammeHistory({
  client,
  programmeId,
  programmeTitle,
  users,
  moduleNames = NO_MODULES,
}: ProgrammeHistoryProps): React.ReactElement {
  const [values, setValues] = useState<HistoryFilterValues>(NO_HISTORY_FILTERS);
  const [limit, setLimit] = useState(PAGE);
  const { events, total, error, pending, reload } = useAuditTrail(
    client,
    historyQueryFor(programmeId, values),
    limit,
  );
  const userNames = new Map(users.map((user) => [user.id, user.displayName]));
  const names = {
    users: userNames,
    programmes: new Map([[programmeId, programmeTitle]]),
    modules: moduleNames,
    tenantName: "",
  };
  const active = historyFiltersActive(values);
  const change = (next: HistoryFilterValues): void => {
    setValues(next);
    // A new filter is a new list; the extra pages were pages of the old one.
    setLimit(PAGE);
  };
  const set =
    <K extends keyof HistoryFilterValues>(key: K) =>
    (value: string): void => {
      change({ ...values, [key]: value });
    };

  function body(): React.ReactElement {
    if (pending && events.length === 0) {
      return (
        <div role="status" aria-label="Loading history" className="space-y-3 py-3">
          {Array.from({ length: 5 }, (_, index) => (
            <div key={`bone-${String(index)}`} aria-hidden="true" className="space-y-2">
              <Bone className="h-4 w-2/3" />
              <Bone className="h-3 w-1/3" />
            </div>
          ))}
        </div>
      );
    }
    if (error) {
      return (
        <ErrorAlert data-testid="history-error">
          <span className="flex flex-wrap items-center gap-3">
            {error}
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                reload();
              }}
            >
              Try again
            </Button>
          </span>
        </ErrorAlert>
      );
    }
    if (events.length === 0) {
      return (
        <EmptyState
          icon="clock-counter-clockwise"
          title={active ? "No events match these filters" : "Nothing has happened here yet"}
          density="row"
          data-testid="history-empty"
        />
      );
    }
    return (
      <div className="space-y-4">
        {groupByDay(events).map((day) => (
          <section key={day.day} aria-label={day.day}>
            <Heading level={3} size="md" className="border-b border-base-300 pb-1">
              {day.day}
            </Heading>
            <ol className="divide-y divide-base-300">
              {day.events.map((event, index) => {
                const target = auditTargetLabel(event, names);
                return (
                  <HistoryEntry
                    // No id on an audit event; its instant and action within
                    // one day's list are as close as the record comes.
                    key={`${event.timestamp}-${event.action}-${String(index)}`}
                    event={event}
                    userNames={userNames}
                    // Every event here is this programme's, so its title says nothing.
                    subject={
                      target === programmeTitle
                        ? undefined
                        : target.replace(` (${programmeTitle})`, "")
                    }
                  />
                );
              })}
            </ol>
          </section>
        ))}
        {events.length < total && limit < MAX_EVENTS ? (
          <Button
            variant="outline"
            size="sm"
            loading={pending}
            onClick={() => {
              setLimit(Math.min(limit + PAGE, MAX_EVENTS));
            }}
            data-testid="history-more"
          >
            Show older events
          </Button>
        ) : null}
      </div>
    );
  }

  return (
    <Panel
      tone="inset"
      headingLevel={2}
      title="History"
      description="Everything that has happened to this programme and its modules, newest first."
      data-testid="programme-history"
    >
      <div className="space-y-4">
        <FilterBar
          aria-label="Filter the history"
          summary="Filter the history"
          variant="flush"
          active={active}
          defaultOpen={active}
          count={pending || error ? undefined : `${events.length} of ${total} shown`}
          countTestId="history-count"
          onClear={() => {
            change(NO_HISTORY_FILTERS);
          }}
          data-testid="history-filters"
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <FormField label="Kind of event" htmlFor="history-filter-category">
              <SelectField
                id="history-filter-category"
                value={values.category}
                onChange={set("category")}
                options={CATEGORY_OPTIONS}
                data-testid="history-filter-category"
              />
            </FormField>
            <FormField label="Person" htmlFor="history-filter-actor">
              <Combobox
                id="history-filter-actor"
                value={values.actor}
                onChange={set("actor")}
                options={[
                  { value: "", label: "Anyone" },
                  ...users.map((user) => ({ value: user.id, label: user.displayName })),
                ]}
                placeholder="Anyone"
                emptyMessage="No people match"
                data-testid="history-filter-actor"
              />
            </FormField>
            <FormField label="From" htmlFor="history-filter-from">
              <TextField
                id="history-filter-from"
                type="date"
                value={values.from}
                onChange={set("from")}
                data-testid="history-filter-from"
              />
            </FormField>
            <FormField
              label="To"
              htmlFor="history-filter-to"
              error={historyRangeBackwards(values) ? "Ends before it starts" : undefined}
            >
              <TextField
                id="history-filter-to"
                type="date"
                value={values.to}
                onChange={set("to")}
                aria-invalid={historyRangeBackwards(values)}
                data-testid="history-filter-to"
              />
            </FormField>
          </div>
        </FilterBar>
        {body()}
      </div>
    </Panel>
  );
}
